<script setup lang="ts">
import type {
  IAppRatingProps,
  IAppRatingEmits,
} from '@/features/AppRating/model/types/appRating';

const props = withDefaults(defineProps<IAppRatingProps>(), {
  modelValue: 0,
  iconWidth: 32,
  iconHeight: 32,
  name: 'rate',
  disabled: false,
  required: true,
});

const emit = defineEmits<IAppRatingEmits>();

const isMobile: boolean = useIsMobile();

const halfRound = (value: number): number => Math.round(value * 2) / 2;

const ratings: number[] = [1, 2, 3, 4, 5];
const tempValue: Ref<number | null> = ref(null);
const rateValue: Ref<number> = ref(halfRound(Number(props.modelValue)));

watch(
  () => props.modelValue,
  (value) => {
    rateValue.value = halfRound(Number(value));
  },
);

const mouseOverHandler = (index: number): void => {
  if (props.disabled) return;
  tempValue.value = rateValue.value;
  rateValue.value = index;
};

const mouseOutHandler = (): void => {
  if (props.disabled) return;
  rateValue.value = tempValue.value ?? 0;
};

const clickHandler = (value: number): void => {
  if (props.disabled) return;
  tempValue.value = value;
  rateValue.value = value;
  emit('update:modelValue', value);
};

const halfStarIndex: ComputedRef<number | null> = computed(() =>
  props.disabled && !Number.isInteger(rateValue.value)
    ? Math.trunc(rateValue.value) + 1
    : null,
);

const getIconName = (rating: number): string =>
  rating === halfStarIndex.value ? 'HalfStar' : 'Star';

const getSymbolId = (rating: number): string =>
  rating === halfStarIndex.value ? '#app-rating-star-half' : '#app-rating-star';

const getViewBox = (rating: number): string =>
  rating === halfStarIndex.value ? '0 0 17 16' : '0 0 16 16';
</script>

<template>
  <div
    v-if="disabled"
    :class="['app-rating', { _mobile: isMobile }]"
    role="img"
    :aria-label="`Рейтинг ${rateValue} из 5`"
  >
    <span
      v-for="rating in ratings"
      :key="rating"
      :class="[
        'app-rating__star',
        '_disabled',
        { _selected: rateValue >= rating && rateValue != null },
      ]"
    >
      <svg
        class="app-icon app-rating__icon"
        width="16"
        height="16"
        :viewBox="getViewBox(rating)"
        fill="none"
        aria-hidden="true"
      >
        <use :href="getSymbolId(rating)" />
      </svg>
    </span>
  </div>

  <div v-else :class="['app-rating', { _mobile: isMobile }]">
    <label
      v-for="rating in ratings"
      :key="rating"
      :class="[
        'app-rating__star',
        { _selected: rateValue >= rating && rateValue != null },
      ]"
      @click.prevent="clickHandler(rating)"
      @mouseover="mouseOverHandler(rating)"
      @mouseout="mouseOutHandler"
    >
      <input
        class="app-rating app-rating__checkbox"
        type="radio"
        :value="rating"
        :name="name"
        :checked="rateValue === rating"
        :aria-label="`Оценка ${rating} из 5`"
        @change="clickHandler(rating)"
      />

      <AppIcon :name="getIconName(rating)" class="app-rating__icon" />
    </label>
  </div>
</template>

<style lang="less">
.app-rating {
  line-height: 100%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  &__star {
    display: inline-flex;
    vertical-align: middle;
    line-height: 1;
    height: inherit;
    align-items: center;
    justify-content: center;

    &:hover {
      cursor: pointer;
    }

    .app-rating__icon {
      stroke-width: 2px;
      color: @color-mustard;
      fill: none;
    }

    &._selected .app-rating__icon {
      transition: fill @A ease-out;
      fill: @color-mustard;

      path {
        transition: stroke @A ease-out;
        stroke: @color-mustard;
      }
    }

    &._disabled:hover {
      cursor: default;
    }
  }

  &__icon {
    transition: fill @A ease-out;
  }

  &__checkbox {
    position: absolute;
    overflow: hidden;
    clip: rect(0 0 0 0);
    height: 1px;
    width: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
  }

  &._mobile &__star {
    width: 13px;
    height: 13px;
  }
}
</style>
